import React, { useState } from 'react';
import {
  Container,
  Grid, Paper, TextField, FormControl, InputLabel,
  Select, MenuItem, Radio, RadioGroup, FormControlLabel,
  Checkbox, Button, Typography, Box, Card, CardContent,
  Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Snackbar, IconButton, useTheme, useMediaQuery,
  Chip, Avatar, Zoom, Slide, Grow, InputAdornment
} from '@mui/material';
import {
  Email, Phone, LocationOn, Work, School, Cake, Send,
  Refresh, ArrowForward, CheckCircle, Computer, Smartphone,
  Tv, BugReport, Cloud, Close, Business, People,
  DataThresholding, Palette, SupportAgent, Analytics, Storage, Security, Pin,
  Person, Description, IntegrationInstructions, Dashboard, ShoppingCart, ChatBubble
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import Navbar from "./Navbar";
import HomeFooter from "./HomeFooter";
import axios from "./Axiosinstance";

// Keyframe animations
const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const glow = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.6;
  }
`;

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Full width HeroSection with animations
const HeroSection = styled(Box)(({ theme }) => ({
  // --- CHANGE 1: Background updated to Light Gray/Blue ---
  background: 'linear-gradient(135deg, #f4f6f8 0%, #e9ecef 100%)',
  color: '#1a1e2b',
  padding: theme.spacing(8, 0),
  position: 'relative',
  overflow: 'hidden',
  width: '100vw',
  marginLeft: 'calc(-50vw + 50%)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 30%, rgba(0,0,0,0.03) 0%, transparent 40%),
                radial-gradient(circle at 80% 70%, rgba(0,0,0,0.02) 0%, transparent 30%)`,
    animation: `${glow} 8s ease-in-out infinite alternate`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, transparent 30%, rgba(0,0,0,0.01) 50%, transparent 70%)',
    animation: `${slideIn} 1s ease-out`,
  }
}));

const FloatingElement = styled(Box)(({ theme }) => ({
  position: 'absolute',
  background: 'radial-gradient(circle, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0) 70%)',
  borderRadius: '50%',
  animation: `${float} 8s ease-in-out infinite`,
  '&:nth-of-type(1)': {
    width: 120,
    height: 120,
    top: '15%',
    left: '5%',
    animationDelay: '0s',
  },
  '&:nth-of-type(2)': {
    width: 180,
    height: 180,
    bottom: '15%',
    right: '8%',
    animationDelay: '2s',
  },
  '&:nth-of-type(3)': {
    width: 100,
    height: 100,
    top: '30%',
    right: '15%',
    animationDelay: '4s',
  },
  '&:nth-of-type(4)': {
    width: 80,
    height: 80,
    bottom: '25%',
    left: '15%',
    animationDelay: '6s',
  },
}));

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.divider}`,
  animation: `${slideIn} 0.6s ease-out`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
    borderColor: theme.palette.primary.main,
    '& .service-icon': {
      transform: 'scale(1.1) rotate(5deg)',
    }
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #2196F3 0%, #21CBF3 100%)',
  color: 'white',
  padding: theme.spacing(1.5, 4),
  fontWeight: 'bold',
  borderRadius: '50px',
  boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
  animation: `${slideIn} 0.5s ease-out 0.3s both`,
  '&:hover': {
    background: 'linear-gradient(45deg, #1976D2 0%, #1EAEDB 100%)',
    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
    transform: 'translateY(-2px)',
  },
}));

const AnimatedTextField = styled(TextField)(({ theme, error }) => ({
  animation: `${slideIn} 0.5s ease-out`,
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      borderWidth: '2px',
      transform: 'scale(1.01)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
      borderWidth: '2px',
      boxShadow: `0 0 0 4px ${error ? theme.palette.error.light : theme.palette.primary.light}20`,
      transform: 'scale(1.01)',
    },
  },
}));

const AnimatedTypography = styled(Typography)(({ theme }) => ({
  animation: `${slideIn} 0.6s ease-out`,
}));

const services = [
  {
    icon: <Computer sx={{ fontSize: 40 }} />,
    title: 'Chart-Topping Apps',
    subtitle: 'WEB APPS',
    description: 'Business Critical Web Apps',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)'
  },
  {
    icon: <Smartphone sx={{ fontSize: 40 }} />,
    title: 'Mobile Apps',
    subtitle: 'MOBILE APPS',
    description: 'iOS and Android Apps',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)'
  },
  {
    icon: <BugReport sx={{ fontSize: 40 }} />,
    title: 'Testing',
    subtitle: 'AUTOMATION TESTING',
    description: 'Quality Assurance Services',
    color: '#F44336',
    gradient: 'linear-gradient(135deg, #F44336 0%, #E57373 100%)'
  },
  {
    icon: <Cloud sx={{ fontSize: 40 }} />,
    title: 'DevOps',
    subtitle: 'DEVOPS',
    description: 'Development To Operations',
    color: '#9C27B0',
    gradient: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)'
  },
  {
    icon: <Security sx={{ fontSize: 40 }} />,
    title: 'Cybersecurity',
    subtitle: 'SECURITY SOLUTIONS',
    description: 'Advanced Security & Protection',
    color: '#FF5722',
    gradient: 'linear-gradient(135deg, #FF5722 0%, #FF8A65 100%)'
  },
  {
    icon: <DataThresholding sx={{ fontSize: 40 }} />,
    title: 'Data Analytics',
    subtitle: 'BIG DATA ANALYTICS',
    description: 'Insights & Business Intelligence',
    color: '#009688',
    gradient: 'linear-gradient(135deg, #009688 0%, #4DB6AC 100%)'
  },
  {
    icon: <Palette sx={{ fontSize: 40 }} />,
    title: 'UI/UX Design',
    subtitle: 'DESIGN SERVICES',
    description: 'User-Centered Design Solutions',
    color: '#E91E63',
    gradient: 'linear-gradient(135deg, #E91E63 0%, #F48FB1 100%)'
  },
  {
    icon: <SupportAgent sx={{ fontSize: 40 }} />,
    title: 'Tech Support',
    subtitle: '24/7 SUPPORT',
    description: 'Round-the-Clock Technical Support',
    color: '#795548',
    gradient: 'linear-gradient(135deg, #795548 0%, #A1887F 100%)'
  },
  {
    icon: <Analytics sx={{ fontSize: 40 }} />,
    title: 'AI & ML',
    subtitle: 'ARTIFICIAL INTELLIGENCE',
    description: 'Smart Automation Solutions',
    color: '#673AB7',
    gradient: 'linear-gradient(135deg, #673AB7 0%, #9575CD 100%)'
  },
  {
    icon: <Storage sx={{ fontSize: 40 }} />,
    title: 'Cloud Services',
    subtitle: 'CLOUD COMPUTING',
    description: 'Scalable Cloud Infrastructure',
    color: '#00BCD4',
    gradient: 'linear-gradient(135deg, #00BCD4 0%, #80DEEA 100%)'
  },
  {
    icon: <IntegrationInstructions sx={{ fontSize: 40 }} />,
    title: 'API Integration',
    subtitle: 'INTEGRATION SERVICES',
    description: 'Seamless System Integration',
    color: '#CDDC39',
    gradient: 'linear-gradient(135deg, #CDDC39 0%, #E6EE9C 100%)'
  },
  {
    icon: <Dashboard sx={{ fontSize: 40 }} />,
    title: 'Dashboard Solutions',
    subtitle: 'ANALYTICS DASHBOARDS',
    description: 'Real-time Monitoring Dashboards',
    color: '#FF9800',
    gradient: 'linear-gradient(135deg, #FF9800 0%, #FFCC80 100%)'
  },
  {
    icon: <ShoppingCart sx={{ fontSize: 40 }} />,
    title: 'E-commerce',
    subtitle: 'ONLINE STORES',
    description: 'Custom E-commerce Platforms',
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #4CAF50 0%, #C8E6C9 100%)'
  },
  {
    icon: <ChatBubble sx={{ fontSize: 40 }} />,
    title: 'Chat Solutions',
    subtitle: 'REAL-TIME CHAT',
    description: 'Instant Messaging Systems',
    color: '#2196F3',
    gradient: 'linear-gradient(135deg, #2196F3 0%, #90CAF9 100%)'
  }
];

const steps = ['Personal Info', 'Education & Experience', 'Review & Submit'];

// Validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^\d{10}$/;
  return phoneRegex.test(phone.replace(/\D/g, ''));
};

const validateZip = (zip) => {
  const zipRegex = /^\d{6}$/;
  return zipRegex.test(zip);
};

const CareersPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    state: '',
    employeeStatus: '',
    dob: '',
    education: '',
    phone: '',
    zip: '',
    gender: '',
    message: '',
    resume: null,
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const fetchStateFromZip = async (zip) => {
    try {
      const res = await fetch(
        `https://api.postalpincode.in/pincode/${zip}`
      );
      const data = await res.json();

      if (data[0].Status === "Success") {
        const stateName = data[0].PostOffice[0].State;

        setFormData((prev) => ({
          ...prev,
          state: stateName,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          state: "",
        }));
      }
    } catch (error) {
      console.error("PIN lookup failed", error);
    }
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Clear error for the field being changed
    setErrors((prev) => ({ ...prev, [name]: "" }));

    let formattedValue = value;

    // Phone number → digits only, max 10
    if (name === "phone") {
      formattedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    // ZIP / PIN → digits only, max 6
    if (name === "zip") {
      formattedValue = value.replace(/\D/g, "").slice(0, 6);

      // 🔥 Auto fetch state when PIN is complete
      if (formattedValue.length === 6) {
        fetchStateFromZip(formattedValue);
      } else {
        // Clear state if PIN is incomplete
        setFormData((prev) => ({
          ...prev,
          state: "",
        }));
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : formattedValue,
    }));
  };


  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

        if (!formData.email.trim()) {
          newErrors.email = 'Email is required';
        } else if (!validateEmail(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
          newErrors.phone = 'Phone number is required';
        } else if (!validatePhone(formData.phone)) {
          newErrors.phone = 'Please enter a valid 10-digit phone number';
        }

        break;

      case 1:
        if (!formData.education.trim()) newErrors.education = 'Education details are required';
        if (!formData.employeeStatus.trim()) newErrors.employeeStatus = 'Employment status is required';

        if (formData.zip && !validateZip(formData.zip)) {
          newErrors.zip = 'Please enter a valid 6-digit zip code';
        }

        break;

      default:
        break;
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setSnackbarMessage('Please fix the errors before proceeding');
      setOpenSnackbar(true);
      return false;
    }

    return true;
  };



  const handleSubmit = async () => {
    if (!formData.agreeToTerms) {
      setSnackbarMessage("Please agree to terms and conditions");
      setOpenSnackbar(true);
      return;
    }
    let resumeUrl = "";

    if (formData.resume) {
      const file = formData.resume;

      const originalFullName = file.name;
      const lastDot = originalFullName.lastIndexOf(".");
      const baseName =
        lastDot === -1
          ? originalFullName
          : originalFullName.slice(0, lastDot);

      const sanitize = (s) =>
        s
          .toLowerCase()
          .replace(/[^a-z0-9-_\.]/g, "-")
          .replace(/-+/g, "-")
          .replace(/(^-|-$)/g, "");

      const publicId = sanitize(baseName);

      const uploadData = new FormData();
      uploadData.append("file", file);
      uploadData.append("upload_preset", "school_LMS");
      uploadData.append("public_id", publicId);
      uploadData.append("cloud_name", "dn8zkmwt1");

      const uploadType =
        file.type === "application/pdf" ? "raw" : "image";

      try {
        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/dn8zkmwt1/${uploadType}/upload`,
          {
            method: "POST",
            body: uploadData,
          }
        );

        const uploadedFile = await uploadResponse.json();

        if (!uploadedFile.secure_url) {
          throw new Error("Resume upload failed");
        }

        resumeUrl = uploadedFile.secure_url;
      } catch (error) {
        console.error("Cloudinary Upload Error:", error);

        setSnackbarMessage("Resume upload failed");
        setOpenSnackbar(true);
        return;
      }
    }
    // Final validation before submission
    const finalErrors = {};

    if (!formData.firstName.trim())
      finalErrors.firstName = "First name is required";

    if (!formData.lastName.trim())
      finalErrors.lastName = "Last name is required";

    if (!formData.email.trim()) {
      finalErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      finalErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      finalErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      finalErrors.phone = "Invalid phone number";
    }

    if (!formData.education.trim())
      finalErrors.education = "Education details are required";

    if (!formData.employeeStatus.trim())
      finalErrors.employeeStatus = "Employment status is required";

    if (!formData.resume) {
      finalErrors.resume = "Resume is required";
    }
    setErrors(finalErrors);

    if (Object.keys(finalErrors).length > 0) {
      setSnackbarMessage("Please fix all errors before submitting");
      setOpenSnackbar(true);
      setActiveStep(0);
      return;
    }

    // ✅ API payload mapping (VERY IMPORTANT)
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phoneNumber: formData.phone,
      dateOfBirth: formData.dob,
      gender: formData.gender,
      education: formData.education,
      employmentStatus: formData.employeeStatus,
      state: formData.state,
      address: formData.address,
      zipCode: formData.zip,
      message: formData.message,
      resumeUrl,
      termsAccepted: formData.agreeToTerms,
    };

    try {
      const response = await axios.post(
        "/api/careers/applications",
        payload
      );

      console.log("API Success:", response.data);
      handleReset();
      setOpenDialog(true);
      setSnackbarMessage("Application submitted successfully!");
      setOpenSnackbar(true);

    } catch (error) {
      console.error("API Error:", error);

      setSnackbarMessage(
        error.response?.data?.message ||
        "Failed to submit application. Please try again."
      );
      setOpenSnackbar(true);
    }
  };



  const handleReset = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      address: '',
      state: '',
      employeeStatus: '',
      dob: '',
      education: '',
      phone: '',
      zip: '',
      gender: '',
      message: '',
      resume: null,
      agreeToTerms: false
    });
    setErrors({});
    setActiveStep(0);
    setSnackbarMessage('Form has been reset');
    setOpenSnackbar(true);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter your first name"
                  variant="outlined"
                  required
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: errors.firstName ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter your last name"
                  variant="outlined"
                  required
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: errors.lastName ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="E-Mail"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  variant="outlined"
                  required
                  error={!!errors.email}
                  helperText={errors.email || 'Format: name@domain.com'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: errors.email ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="1234567890"
                  variant="outlined"
                  required
                  error={!!errors.phone}
                  helperText={errors.phone || '10 digits only'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: errors.phone ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    inputProps: {
                      maxLength: 10,
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="Date Of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Cake sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 0.5, fontWeight: 'medium' }}>
                  Gender:
                </Typography>
                <RadioGroup
                  row
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  sx={{ justifyContent: 'space-around' }}
                >
                  <FormControlLabel value="male" control={<Radio />} label="Male" />
                  <FormControlLabel value="female" control={<Radio />} label="Female" />
                  <FormControlLabel value="others" control={<Radio />} label="Others" />
                </RadioGroup>
              </Grid>
            </Grid>
          </Slide>
        );

      case 1:
        return (
          <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AnimatedTextField
                  fullWidth
                  label="Education"
                  name="education"
                  value={formData.education}
                  onChange={handleChange}
                  placeholder="Enter your Education details (e.g., Bachelor's in Computer Science)"
                  variant="outlined"
                  multiline
                  rows={3}
                  required
                  error={!!errors.education}
                  helperText={errors.education}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <School sx={{ color: errors.education ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!errors.employeeStatus}>
                  <InputLabel>What Is Your Current Employment Status?</InputLabel>
                  <Select
                    name="employeeStatus"
                    value={formData.employeeStatus}
                    onChange={handleChange}
                    label="What Is Your Current Employee Status?"
                  >
                    <MenuItem value="employed">Employed</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                    <MenuItem value="student">Student</MenuItem>
                    <MenuItem value="self-employed">Self-Employed</MenuItem>
                    <MenuItem value="freelancer">Freelancer</MenuItem>
                  </Select>
                  {errors.employeeStatus && (
                    <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                      {errors.employeeStatus}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="Zip Code"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  placeholder="123456"
                  variant="outlined"
                  error={!!errors.zip}
                  helperText={errors.zip || '6 digits only'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Pin sx={{ color: errors.zip ? 'error.main' : 'text.secondary' }} />
                      </InputAdornment>
                    ),
                    inputProps: {
                      maxLength: 6,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <AnimatedTextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your full address"
                  variant="outlined"
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AnimatedTextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData.state}
                  variant="outlined"
                  disabled
                />
              </Grid>
            </Grid>
          </Slide>
        );

      case 2:
        return (
          <Slide direction="up" in={true} mountOnEnter unmountOnExit>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <AnimatedTextField
                  fullWidth
                  label="Message (Comment)"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Enter any message briefly (Optional)"
                  variant="outlined"
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                >
                  Upload Resume (PDF/DOC/DOCX)
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        resume: e.target.files[0],
                      }))
                    }
                  />
                </Button>

                {formData.resume && (
                  <Typography variant="body2" mt={1}>
                    Selected: {formData.resume.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Grow in={true}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="agreeToTerms"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the <Box component="span" sx={{ color: 'primary.main', fontWeight: 'medium' }}>Terms and Conditions</Box>
                      </Typography>
                    }
                  />
                </Grow>
              </Grid>
              <Grid item xs={12}>
                <Zoom in={true}>
                  <Paper sx={{ p: 3, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                      <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Application Summary
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Name:</Typography>
                        <Typography variant="body1" fontWeight="medium">{formData.firstName} {formData.lastName}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Email:</Typography>
                        <Typography variant="body1" fontWeight="medium">{formData.email}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Phone:</Typography>
                        <Typography variant="body1" fontWeight="medium">{formData.phone || 'Not provided'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{ textTransform: 'capitalize' }}>
                          {formData.employeeStatus || 'Not specified'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Education:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.education ? `${formData.education.substring(0, 50)}...` : 'Not provided'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">Location:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formData.state ? `${formData.state}, ${formData.zip}` : 'Not specified'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Zoom>
              </Grid>
            </Grid>
          </Slide>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      <Navbar />

      {/* Full Width Hero Section */}
      <HeroSection>
        <FloatingElement />
        <FloatingElement />
        <FloatingElement />
        <FloatingElement />

        <Container maxWidth="xl" >
        <Grid container alignItems="center" spacing={4} mt={2}>
  {/* LEFT SIDE - TEXT CONTENT */}
  <Grid item xs={12} md={8}>
    <Box
      sx={{
        animation: `${slideIn} 0.8s ease-out`,
        textAlign: { xs: 'center', md: 'left' }, // ✅ FIX: Center on mobile, left on desktop
      }}
    >


      <AnimatedTypography
  variant="h1"                    // ✅ same variant
  component="h1"
  fontWeight="bold"
  sx={{
    fontSize: { xs: '2.2rem', md: '3.5rem' },  // smaller
    lineHeight: 1.3,
    color: '#1a237e',
    m: 0,
    mb: 0.5,
  }}
>
  Build Your Career With
</AnimatedTypography>

<AnimatedTypography
  variant="h1"                    // ✅ same variant
  component="h2"
  fontWeight="bold"
  sx={{
    fontSize: { xs: '2.8rem', md: '4.5rem' },  // bigger
    lineHeight: 1.15,
    color: '#1a237e',
    m: 0,
    mb: 3,
  }}
>
  Lider Technology
</AnimatedTypography>

      <AnimatedTypography
        variant="h5"
        sx={{
          opacity: 0.9,
          mb: 3,
          fontSize: { xs: '1.2rem', md: '1.8rem' },
          animation: `${slideIn} 0.8s ease-out 0.5s both`,
          color: '#5a6b7c',
          lineHeight: 1.4, // ✅ FIX
          textAlign: { xs: 'center', md: 'left' }, // ✅ FIX
        }}
      >
        Solutions Pvt. Limited
      </AnimatedTypography>

      <AnimatedTypography
        variant="h6"
        sx={{
          opacity: 0.8,
          mb: 5,
          maxWidth: { xs: '100%', md: '600px' }, // ✅ FIX: Full width on mobile
          mx: { xs: 'auto', md: 0 }, // ✅ FIX: Center block on mobile
          fontSize: { xs: '1rem', md: '1.2rem' },
          animation: `${slideIn} 0.9s ease-out 0.6s both`,
          color: '#5a6b7c',
          lineHeight: 1.6, // ✅ FIX: Better readability
          textAlign: { xs: 'center', md: 'left' }, // ✅ FIX
        }}
      >
        Innovate with us. Create solutions that matter. Transform industries.
        Be part of our journey in building cutting-edge digital experiences.
      </AnimatedTypography>

      {/* CHIPS CONTAINER */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          animation: `${slideIn} 1s ease-out 0.7s both`,
          justifyContent: { xs: 'center', md: 'flex-start' }, // ✅ FIX: Center chips on mobile
        }}
      >
        <Chip
          label="Competitive Salary"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            color: '#1a237e',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-2px)'
            }
          }}
          icon={<Business />}
        />
        <Chip
          label="Great Culture"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            color: '#1a237e',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-2px)'
            }
          }}
          icon={<People />}
        />
        <Chip
          label="Growth Opportunities"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.05)',
            color: '#1a237e',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-2px)'
            }
          }}
          icon={<Work />}
        />
      </Box>
    </Box>
  </Grid>

  {/* RIGHT SIDE - AVATAR */}
  <Grid item xs={12} md={4}>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        animation: `${slideIn} 1.2s ease-out 0.8s both`,
      }}
    >
      <Avatar
        sx={{
          width: { xs: 160, md: 240 }, // ✅ FIX: Smaller on mobile
          height: { xs: 160, md: 240 },
          bgcolor: 'white',
          color: '#1a237e',
          fontSize: { xs: '2.5rem', md: '3.5rem' }, // ✅ FIX: Responsive font
          fontWeight: 'bold',
          boxShadow: '0 15px 50px rgba(0,0,0,0.1)',
          border: '6px solid rgba(0,0,0,0.05)',
          transition: 'all 0.5s ease',
          '&:hover': {
            transform: 'scale(1.05) rotate(5deg)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        LTS
      </Avatar>
    </Box>
  </Grid>
</Grid>
        </Container>
      </HeroSection>

      {/* Rest of the content */}
      <Container maxWidth="lg">

        {/* Application Form Section */}
        <Paper elevation={0} sx={{
          p: { xs: 3, md: 6 },
          borderRadius: 3,
          bgcolor: 'white',
          boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0',
          mt: 8
        }}>
          <Typography variant="h3" gutterBottom align="center" color="primary" fontWeight="bold" sx={{ mb: 1 }}>
            Start Your Journey
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Complete the form below to apply for exciting opportunities at Lider Technology
          </Typography>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              mb: { xs: 3, md: 6 },

              "& .MuiStepLabel-label": {
                fontSize: {
                  xs: "0.65rem",
                  sm: "0.8rem",
                  md: "0.9rem",
                },
                textAlign: "center",
              },

              "& .MuiStepIcon-root": {
                fontSize: {
                  xs: "1.5rem",
                  md: "2rem",
                },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>
                  <Typography
                    sx={{
                      fontSize: {
                        xs: "0.7rem",
                        sm: "0.8rem",
                        md: "0.9rem",
                      },
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Form Content */}
          <Box sx={{ mt: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, flexWrap: 'wrap', gap: 2 }}>
            <Button
              onClick={handleReset}
              startIcon={<Refresh />}
              variant="outlined"
              color="secondary"
              size="large"
              sx={{ borderRadius: '50px', px: 4 }}
            >
              Reset Form
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              {activeStep > 0 && (
                <Button
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                  sx={{ borderRadius: '50px', px: 4 }}
                >
                  Back
                </Button>
              )}

              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                  sx={{ borderRadius: '50px', px: 4 }}
                >
                  Continue
                </Button>
              ) : (
                <SubmitButton
                  onClick={handleSubmit}
                  startIcon={<Send />}
                  size="large"
                >
                  Submit Application
                </SubmitButton>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Services Section */}
        <Box sx={{ mb: 8, mt: 8 }}>
          <Typography variant="h3" align="center" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 1 }}>
            Our Core Expertise
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}>
            We deliver exceptional solutions across multiple domains, helping businesses transform digitally
          </Typography>
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <ServiceCard>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box
                      className="service-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '20px',
                        background: service.gradient,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        fontSize: '2.5rem',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Chip
                      label={service.subtitle}
                      size="small"
                      sx={{
                        mb: 2,
                        bgcolor: `${service.color}15`,
                        color: service.color,
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        letterSpacing: '1px'
                      }}
                    />
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      {service.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {service.description}
                    </Typography>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>


      {/* Success Dialog */}
      <Dialog
        open={openDialog}
        onClick={() => {
          setOpenDialog(false);
          handleReset();
        }}
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
          Application Submitted Successfully!
        </DialogTitle>
        <DialogContent sx={{ pt: 4, pb: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <CheckCircle sx={{ fontSize: 60, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Thank You, {formData.firstName}!
            </Typography>
          </Box>
          <Typography variant="body1" gutterBottom align="center">
            Your application has been received and is under review.
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Our HR team will contact you via email within 5-7 business days.
          </Typography>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" align="center">
              Application Reference: LTS-{Date.now().toString().slice(-8)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" align="center">
              Submitted on: {new Date().toLocaleDateString()}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            fullWidth
            size="large"
            sx={{ borderRadius: '50px' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() => setOpenSnackbar(false)}
          sx={{ borderRadius: '50px' }}
          action={
            <IconButton size="small" color="inherit" onClick={() => setOpenSnackbar(false)}>
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <HomeFooter />
    </Box>
  );
};

export default CareersPage;